import React, { useEffect, useState } from "react";
import {
  FaTruck,
  FaFileInvoiceDollar,
  FaTag,
  FaExchangeAlt,
  FaPills,
  FaChartLine,
  FaExclamationCircle,
  FaWarehouse,
  FaLink,
  FaCog,
  FaStore,
} from "react-icons/fa";
import { useGlobalContext } from "../../context/stateContext";
import { useAuth } from "../../context/Auth";
import { MdInventory2 } from "react-icons/md";
import { GiMedicines } from "react-icons/gi";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import ExpiryAlertModal from "../../components/modals/ExpiryAlertModal";

const StoreManagerDashboard = () => {
  const navigate = useNavigate();
  const { activeSession } = useGlobalContext();
  const { requestWithAuth } = useAuth();
  const [gettingDashboardData, setGettingDashboardData] = useState(false);
  const [dashboardData, setDashboardData] = useState(null);
  const [showExpiryAlert, setShowExpiryAlert] = useState(false);

  const getStoreManagerDashboardData = async () => {
    setGettingDashboardData(true);
    try {
      const response = await requestWithAuth(`/users/stores/dashboard`, "GET");
      setDashboardData(response?.data);
    } catch (error) {
      toast.error("Failed to get dashboard data");
    } finally {
      setGettingDashboardData(false);
    }
  };

  useEffect(() => {
    getStoreManagerDashboardData();
  }, []);

  // Check if alert should be shown
  useEffect(() => {
    const checkExpiryAlert = () => {
      const today = new Date().toISOString().split("T")[0];
      const dismissedDate = localStorage.getItem("expiryAlertDismissed");
      const hasExpiryIssues =
        dashboardData?.expiring_drugs > 0 || dashboardData?.expired_drugs > 0;
      const shouldShow = hasExpiryIssues && dismissedDate !== today;

      if (shouldShow) {
        setTimeout(() => {
          setShowExpiryAlert(true);
        }, 500);
      }
    };

    if (!gettingDashboardData) {
      checkExpiryAlert();
    }
  }, [
    gettingDashboardData,
    dashboardData?.expiring_drugs,
    dashboardData?.expired_drugs,
  ]);

  const handleCloseExpiryAlert = () => {
    setShowExpiryAlert(false);
  };

  const handleCloseAndHideExpiryAlert = () => {
    if (dashboardData?.expired_drugs > 0) {
      toast.error(
        `You have ${dashboardData?.expired_drugs} expired drugs, please clear them before continuing`
      );
      return;
    }
    const today = new Date().toISOString().split("T")[0];
    // localStorage.setItem("expiryAlertDismissed", today);
    setShowExpiryAlert(false);
  };

  const handleViewExpiringDrugs = () => {
    const branch_id = activeSession?.branch?.id;
    if (dashboardData?.expired_drugs === 0) {
      const today = new Date().toISOString().split("T")[0];
      // localStorage.setItem("expiryAlertDismissed", today);
    }
    setShowExpiryAlert(false);
    navigate(`/inventory/branch/expiring-drugs/${branch_id}`);
  };

  const StatCard = ({
    title,
    value,
    subtitle,
    additionalInfo,
    icon: Icon,
    color,
    linkTo,
  }) => (
    <div
      className={`rounded-lg p-6 ${color} text-white shadow-lg hover:shadow-xl transition-shadow`}
    >
      <div className="flex items-center justify-between mb-4">
        <Icon className="text-2xl opacity-80" />
        <div className="text-right">
          <h3 className="text-2xl font-bold">{value}</h3>
          <p className="text-sm opacity-90">{title}</p>
        </div>
      </div>
      {subtitle && <p className="text-xs opacity-75 mb-2">{subtitle}</p>}
      {additionalInfo && (
        <p className="text-xs opacity-75 mb-3">{additionalInfo}</p>
      )}
      {linkTo && (
        <button
          onClick={() => (window.location.href = `${linkTo}`)}
          className="inline-flex items-center gap-1 text-sm hover:underline cursor-pointer"
        >
          Manage <FaLink className="text-[10px]" />
        </button>
      )}
    </div>
  );

  const SimpleCard = ({
    title,
    value,
    linkTo,
    color = "bg-gray-100",
    subtitle,
    icon: Icon,
  }) => (
    <div
      className={`rounded-lg p-6 ${color} text-center shadow-md hover:shadow-lg transition-shadow`}
    >
      <div className="flex justify-center mb-3">
        {Icon && <Icon className="text-3xl text-gray-600" />}
      </div>
      <h3 className="text-2xl font-bold mb-2 text-gray-800">{value}</h3>
      <p className="text-sm text-gray-600 mb-3">{title}</p>
      {subtitle && <p className="text-xs text-gray-500 mb-3">{subtitle}</p>}
      {linkTo && (
        <button
          onClick={() => (window.location.href = `${linkTo}`)}
          className="inline-flex items-center gap-1 text-sm text-blue-600 hover:underline cursor-pointer"
        >
          Access <FaLink className="text-[12px]" />
        </button>
      )}
    </div>
  );

  if (gettingDashboardData) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900 mb-2 flex items-center gap-2">
            <FaStore className="text-blue-600" />
            Store Manager Dashboard
          </h1>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {[1, 2, 3, 4].map((i) => (
            <div
              key={i}
              className="bg-gray-200 rounded-lg h-36 animate-pulse"
            ></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto min-h-screen">
      <ExpiryAlertModal
        isOpen={showExpiryAlert}
        onClose={handleCloseExpiryAlert}
        onHideModal={handleCloseAndHideExpiryAlert}
        expiringDrugs={dashboardData?.expiring_drugs}
        expiredDrugs={dashboardData?.expired_drugs}
        onViewDetails={handleViewExpiringDrugs}
      />
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2 flex items-center gap-3">
          <FaStore className="text-blue-600" />
          Store Manager Dashboard
        </h1>
        <p className="text-gray-600">
          Inventory and stock management control center
        </p>
      </div>

      {/* Core Store Functions */}
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-2 flex items-center gap-2">
          <FaCog className="text-gray-600" />
          Core Store Functions
        </h2>
        <p className="text-sm text-gray-600 mb-4">
          Primary stock and invoice management operations
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <StatCard
          title="Pending Stock Requisition"
          value={dashboardData?.pending_requisitions || "0"}
          subtitle="Pending stock requests from warehouse"
          additionalInfo="Process inventory requests"
          icon={FaTruck}
          color="bg-blue-600"
          linkTo="/store-manager/requisition/pending"
        />

        <StatCard
          title="Supplier Invoice Entries"
          value={dashboardData?.supplier_invoices || "0"}
          additionalInfo="Process supplier invoices"
          icon={FaFileInvoiceDollar}
          color="bg-green-600"
          linkTo="/inventory/invoice"
        />

        <StatCard
          title="Drugs In Stocks"
          value={dashboardData?.stores_drugs || "0"}
          subtitle="Drugs in stock"
          additionalInfo="Manage stock levels"
          icon={FaTag}
          color="bg-purple-600"
          linkTo={`/inventory/branch/${activeSession?.branch?.id}`}
        />
        <StatCard
          title="Rejected Requisitions"
          value={dashboardData?.rejected_requisitions || "0"}
          subtitle="Rejected requisitions"
          additionalInfo="Manage rejected requisitions"
          icon={FaTag}
          color="bg-red-600"
          linkTo={`/store-manager/requisition/rejected`}
        />
      </div>

      {/* Inventory Management */}
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-2">
          Inventory Management
        </h2>
        <p className="text-sm text-gray-600 mb-4">
          Drug creation, reorder levels, and expiry tracking
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <SimpleCard
          title="Create New Drugs"
          value="Manage"
          subtitle="Add new drugs to inventory"
          linkTo="/administration/inventory/"
          color="bg-white border-2 border-blue-200 text-blue-800"
          icon={GiMedicines}
        />

        <SimpleCard
          title="Reorder Levels"
          value={dashboardData?.drugs_below_reorder || "0"}
          subtitle="Drugs below reorder level"
          linkTo="/inventory/reorder/level"
          color="bg-white border-2 border-yellow-200 text-yellow-800"
          icon={FaChartLine}
        />

        <SimpleCard
          title="Expiry Management"
          value={dashboardData?.expiring_drugs || "0"}
          subtitle="Drugs nearing expiry"
          linkTo={`/inventory/branch/expiring-drugs/${activeSession?.branch?.id}`}
          color="bg-white border-2 border-red-200 text-red-800"
          icon={FaExclamationCircle}
        />

        <SimpleCard
          title="Supply to Warehouse"
          value={dashboardData?.pending_warehouse_supplies || "0"}
          subtitle="Pending warehouse transfers"
          linkTo="/store-manager/requisition/pending"
          color="bg-white border-2 border-green-200 text-green-800"
          icon={FaWarehouse}
        />
      </div>

      {/* Quick Actions */}
      <div className="mt-8 bg-white rounded-lg p-6 shadow-md">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Quick Actions
        </h3>
        <div className="flex flex-wrap gap-3">
          <button
            onClick={() =>
              (window.location.href = "/administration/inventory/create")
            }
            className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-blue-700 transition-colors cursor-pointer"
          >
            Create New Drug
          </button>

          <button
            onClick={() =>
              (window.location.href = "/administration/inventory/")
            }
            className="bg-purple-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-purple-700 transition-colors cursor-pointer"
          >
            Update Prices
          </button>
          <button
            onClick={() =>
              (window.location.href = `/inventory/branch/expiring-drugs/${activeSession?.branch?.id}`)
            }
            className="bg-red-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-red-700 transition-colors cursor-pointer"
          >
            Check Expiries
          </button>
          <button
            onClick={() => (window.location.href = "/inventory/reorder/level")}
            className="bg-orange-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-orange-700 transition-colors cursor-pointer"
          >
            Manage Reorder Levels
          </button>
        </div>
      </div>
    </div>
  );
};

export default StoreManagerDashboard;
